import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ToastService } from 'src/app/shared/services/toast.service';
import { DisplayUser } from '../shared/models/displayUserModel';
import { UserLobbySettingsModel } from '../shared/models/lobbySettingsModel';
import { UserService } from '../shared/services/user.service';
import { LobbySettingsService } from '../../../shared/services/lobby-settings.service';
import { GetUserPermissionList } from '../../../state';
import { Store } from '@ngrx/store';
import { State } from '../../../state/app.state';
import { FormsModule } from '@angular/forms';
import { TranslationLoaderFacade } from '../../localization/translation-loader.facade';
import { Subscription } from 'rxjs';
import { LobbyTabConfig } from 'src/app/shared/models/default-lobby-tab';

@Component({
  selector: 'app-user-lobby-settings',
  templateUrl: './user-lobby-settings.component.html',
  styleUrls: ['./user-lobby-settings.component.scss']
})
export class UserLobbySettingsComponent implements OnInit {
  @Output() dirtyEmitter = new EventEmitter<boolean>(false);
  @Output() closeEmitter = new EventEmitter();

  userLobbySettings: FormGroup = new FormGroup({
    supervisor: new FormControl(),
    showInKiosk: new FormControl(),
    showUserInLobby: new FormControl(),
    defaultLobbyTab: new FormControl(),
    showAsSupervisor: new FormControl(),
    enableUserImpersonation: new FormControl(),
    showUserForImpersonation: new FormControl()
  });
  public globalUserImpersonationEnabled = true;
  public defaultLobbyTabsMerges : LobbyTabConfig[] = []; 
  public defaultNewLobbyTabs : LobbyTabConfig[]= []; 
  public supervisorList: DisplayUser[] = [];
  public userPermissionList: number[] = [];
  public editingUserId: number | undefined;
  public ngDefaultSelected: string = '';
  private subscriptions = new Subscription();

  constructor(
    public fb: FormBuilder,
    private store: Store<State>,  
    private userService: UserService, 
    public toastService: ToastService, 
    public lobbySettingsService: LobbySettingsService,
    private translationLoaderFacade: TranslationLoaderFacade
    ) {
    this.generateSettingsForm();
    this.defaultLobbyTabsMerges = lobbySettingsService.getDefaultLobbyTabsMerges();
    this.defaultNewLobbyTabs = lobbySettingsService.getNewDefaultLobbyTabs();
  }


  ngOnInit() {
    this.loadLobbySettings();
  }

  public loadLobbySettings() {
    let editingUserdata = this.userService.getEditingUser();
    this.subscriptions.add(
      this.userService.getSupervisors().subscribe({
        next: (supervisors) => {
          this.supervisorList = supervisors;
          this.patchValues();
        }
      })
    );

    if (editingUserdata) {
      this.editingUserId = editingUserdata?.userDetails?.userId;
      this.checkForDirty();
    }
  }

  /*
  * Generate tenant creation form controllers
  */
  public generateSettingsForm() {
    this.userLobbySettings = this.fb.group({
      showUserInLobby: new FormControl(true),
      showInKiosk: new FormControl(true),
      defaultLobbyTab: new FormControl(),
      supervisor: new FormControl(""),
      showAsSupervisor: new FormControl(true),
      enableUserImpersonation: new FormControl(false),
      showUserForImpersonation: new FormControl(false)
    });
  }

  /**
   * Load the editing users users data
   */
  public patchValues() {
    let editingUserdata = this.userService.getEditingUser();
    if (editingUserdata) {
      this.editingUserId = editingUserdata?.userDetails?.userId;
      this.userService.getEditingUserDetails(this.editingUserId).subscribe((userData) => {
        this.userLobbySettings.patchValue({
          showUserInLobby: userData.userDetails.showInLobby,
          showInKiosk: userData.userDetails.showInKiosk,
          defaultLobbyTab: userData.userDetails.defaultLobbyTab,
          ngDefaultSelected: userData.userDetails.defaultLobbyTab,
          supervisor: this.supervisorList.some(i => i.userId === userData.userDetails.supervisorId) ? this.supervisorList.filter(s => s.userId == userData.userDetails.supervisorId)[0] : "",
          showAsSupervisor: userData.userDetails.isSupervisor,
          enableUserImpersonation: userData.userDetails.enableUserImpersonation,
          showUserForImpersonation: userData.userDetails.showUserForImpersonation
        });
        this.globalUserImpersonationEnabled = userData.userDetails.globalUserImpersonationEnabled;
        if (editingUserdata?.userDetails?.userPermissionIds == null) {
          throw Error("userPermissionIds was null! Which was not expected!")
        }
        this.generateDefaultLobbyTabWithUserPermisson(editingUserdata.userDetails?.userPermissionIds);
      });
    }
  }



  /**
   * Notify if there are unsaved changed in this child form to the parent form
   */
  public checkForDirty() {
    this.userLobbySettings.valueChanges.subscribe(value => {
      this.dirtyEmitter.emit(this.userLobbySettings.dirty);
    });
  }

  /**
   * Save unsave changes of Lobby Settings
   */
  public submitUserLobbySettings() {
    if (this.editingUserId == undefined) {
      throw Error("this.editingUserId was undefined! Which was not expected!")
    }

    let userLobbySettings = new UserLobbySettingsModel();
    userLobbySettings.UserId = this.editingUserId;
    userLobbySettings.ShowInLobby = this.userLobbySettings.controls.showUserInLobby.value;
    userLobbySettings.ShowInKiosk = this.userLobbySettings.controls.showInKiosk.value;
    userLobbySettings.DefaultLobbyTab = this.userLobbySettings.controls.defaultLobbyTab.value;
    userLobbySettings.SupervisorId = this.userLobbySettings.controls.supervisor.value.userId;
    userLobbySettings.IsSupervisor = this.userLobbySettings.controls.showAsSupervisor.value;
    userLobbySettings.EnableUserImpersonation = this.userLobbySettings.controls.enableUserImpersonation.value;
    userLobbySettings.ShowUserForImpersonation = this.userLobbySettings.controls.showUserForImpersonation.value;
    this.userService.updateLobbySettings(userLobbySettings, this.editingUserId).subscribe((data) => {
      if (this.editingUserId == undefined) {
        throw Error("this.editingUserId was undefined! Which was not expected!")
      }
      this.userService.getEditingUserDetails(this.editingUserId).subscribe((userData) => {
        this.userService.setEditingUser(userData);
        this.toastService.showSuccess('_General_Changes_Saved_Successfully_Ts_Text_');
        this.userLobbySettings.markAsPristine();
        this.dirtyEmitter.emit(false);
        this.userService.getSupervisors().subscribe(result => {
          this.supervisorList = result
          this.userLobbySettings.patchValue({
            supervisor: this.supervisorList.some(i => i.userId === userData.userDetails.supervisorId) ? this.supervisorList.filter(s => s.userId == userData.userDetails.supervisorId)[0] : ""
          })
        });

      },
        error => {
          console.log(error);
          this.toastService.showError('_General_Saving_Failed_Ts_Text_');

        })
    },
    error => {
      console.log(error);
      this.toastService.showError('_General_Saving_Failed_Ts_Text_');

      });
  }


  /**
   * Close the accordion panel without reverting the changes when clicking on the Close button
   */
  public closeClicked() {
    this.closeEmitter.emit('ngb-panel-2');
  }

  public generateDefaultLobbyTabWithUserPermisson(userPermission: number[]) {
    this.store.select(GetUserPermissionList).subscribe((allUserPermissions) => {
      if (allUserPermissions == undefined) {
        throw Error("allUserPermissions was undefined! Which was not expected!")
      }
      this.userPermissionList = allUserPermissions;
      if (allUserPermissions.includes(47) && (userPermission != null && userPermission.length > 0 && userPermission?.includes(47)))
        this.defaultLobbyTabsMerges = this.defaultLobbyTabsMerges;

      else {
        this.defaultLobbyTabsMerges = this.defaultNewLobbyTabs;
        if (this.ngDefaultSelected != '2')
          this.ngDefaultSelected = '1';
      }
    })
  }
}
